import Repository from "./Repository";
import {
  AllergenAdminDashboardResponse,
  AllergenAdminJobSummary,
  AllergenAdminResolveReviewRequest,
  AllergenAdminReviewQueueResponse,
  AllergenAdminRunJobRequest,
  AnalyzeIngredientMlRequest,
  AnalyzeRecipeMlRequest,
  CountryAllergenSymptomsItem,
  MlModelStatusResponse,
  MlAnalysisResponse,
  OverrideIngredientAllergenRequest,
  OverrideRecipeIngredientAllergenRequest,
  TestAllergenMlModelRequest,
  TestAllergenMlModelResponse,
  TrainAllergenMlModelRequest,
  TrainAllergenMlModelResponse
} from "@/models/AllergenML/AllergenML";
import { AxiosResponse } from "axios";

const resource = "/AllergenML";

export default {
  analyzeIngredient(request: AnalyzeIngredientMlRequest): Promise<AxiosResponse<MlAnalysisResponse>> {
    return Repository.post<MlAnalysisResponse>(
      `${resource}/AnalyzeIngredient`,
      request,
      { withCredentials: true }
    );
  },
  analyzeRecipe(request: AnalyzeRecipeMlRequest): Promise<AxiosResponse<MlAnalysisResponse>> {
    return Repository.post<MlAnalysisResponse>(
      `${resource}/AnalyzeRecipe`,
      request,
      { withCredentials: true }
    );
  },
  getSavedIngredientAnalysis(request: AnalyzeIngredientMlRequest): Promise<AxiosResponse<MlAnalysisResponse>> {
    return Repository.post<MlAnalysisResponse>(
      `${resource}/SavedIngredientAnalysis`,
      request,
      { withCredentials: true }
    );
  },
  getSavedRecipeAnalysis(request: AnalyzeRecipeMlRequest): Promise<AxiosResponse<MlAnalysisResponse>> {
    return Repository.post<MlAnalysisResponse>(
      `${resource}/SavedRecipeAnalysis`,
      request,
      { withCredentials: true }
    );
  },
  getCountryAllergenSymptoms(countryId?: number | null, nutrientSource?: number | null): Promise<AxiosResponse<CountryAllergenSymptomsItem[]>> {
    return Repository.get<CountryAllergenSymptomsItem[]>(
      `${resource}/CountryAllergenSymptoms`,
      {
        params: { countryId, nutrientSource },
        withCredentials: true
      }
    );
  },
  overrideRecipeIngredientAllergen(request: OverrideRecipeIngredientAllergenRequest): Promise<AxiosResponse<MlAnalysisResponse>> {
    return Repository.post<MlAnalysisResponse>(
      `${resource}/OverrideRecipeIngredientAllergen`,
      request,
      { withCredentials: true }
    );
  },
  overrideIngredientAllergen(request: OverrideIngredientAllergenRequest): Promise<AxiosResponse<MlAnalysisResponse>> {
    return Repository.post<MlAnalysisResponse>(
      `${resource}/OverrideIngredientAllergen`,
      request,
      { withCredentials: true }
    );
  },
  getAdminDashboard(): Promise<AxiosResponse<AllergenAdminDashboardResponse>> {
    return Repository.get<AllergenAdminDashboardResponse>(
      `${resource}/AdminDashboard`,
      { withCredentials: true }
    );
  },
  trainModel(request: TrainAllergenMlModelRequest): Promise<AxiosResponse<TrainAllergenMlModelResponse>> {
    return Repository.post<TrainAllergenMlModelResponse>(
      `${resource}/TrainModel`,
      request,
      { withCredentials: true }
    );
  },
  testModel(request: TestAllergenMlModelRequest): Promise<AxiosResponse<TestAllergenMlModelResponse>> {
    return Repository.post<TestAllergenMlModelResponse>(
      `${resource}/TestModel`,
      request,
      { withCredentials: true }
    );
  },
  exportTestModelReport(request: TestAllergenMlModelRequest): Promise<AxiosResponse<Blob>> {
    return Repository.post<Blob>(
      `${resource}/ExportTestModelReport`,
      request,
      {
        withCredentials: true,
        responseType: "blob"
      }
    );
  },
  trainModelWithTemplate(formData: FormData): Promise<AxiosResponse<TrainAllergenMlModelResponse>> {
    return Repository.post<TrainAllergenMlModelResponse>(
      `${resource}/TrainModelWithTemplate`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      }
    );
  },
  testModelWithTemplate(formData: FormData): Promise<AxiosResponse<TestAllergenMlModelResponse>> {
    return Repository.post<TestAllergenMlModelResponse>(
      `${resource}/TestModelWithTemplate`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      }
    );
  },
  exportTestModelReportWithTemplate(formData: FormData): Promise<AxiosResponse<Blob>> {
    return Repository.post<Blob>(
      `${resource}/ExportTestModelReportWithTemplate`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob"
      }
    );
  },
  downloadTrainingTemplate(): Promise<AxiosResponse<Blob>> {
    return Repository.get<Blob>(
      `${resource}/DownloadTrainingTemplate`,
      {
        withCredentials: true,
        responseType: "blob"
      }
    );
  },
  getModelStatus(): Promise<AxiosResponse<MlModelStatusResponse>> {
    return Repository.get<MlModelStatusResponse>(
      `${resource}/ModelStatus`,
      { withCredentials: true }
    );
  },
  getReviewQueue(search: string, page: number, pageSize: number): Promise<AxiosResponse<AllergenAdminReviewQueueResponse>> {
    return Repository.get<AllergenAdminReviewQueueResponse>(
      `${resource}/ReviewQueue`,
      {
        params: { search, page, pageSize },
        withCredentials: true
      }
    );
  },
  runSetIngredientAllergens(request: AllergenAdminRunJobRequest): Promise<AxiosResponse<AllergenAdminJobSummary>> {
    return Repository.post<AllergenAdminJobSummary>(
      `${resource}/RunSetIngredientAllergens`,
      request,
      { withCredentials: true }
    );
  },
  pauseJob(jobId: number): Promise<AxiosResponse<AllergenAdminJobSummary>> {
    return Repository.post<AllergenAdminJobSummary>(
      `${resource}/PauseJob/${jobId}`,
      {},
      { withCredentials: true }
    );
  },
  cancelJob(jobId: number): Promise<AxiosResponse<AllergenAdminJobSummary>> {
    return Repository.post<AllergenAdminJobSummary>(
      `${resource}/CancelJob/${jobId}`,
      {},
      { withCredentials: true }
    );
  },
  resumeJob(jobId: number): Promise<AxiosResponse<AllergenAdminJobSummary>> {
    return Repository.post<AllergenAdminJobSummary>(
      `${resource}/ResumeJob/${jobId}`,
      {},
      { withCredentials: true }
    );
  },
  continueJob(jobId: number): Promise<AxiosResponse<AllergenAdminJobSummary>> {
    return Repository.post<AllergenAdminJobSummary>(
      `${resource}/ContinueJob/${jobId}`,
      {},
      { withCredentials: true }
    );
  },
  resolveReview(request: AllergenAdminResolveReviewRequest): Promise<AxiosResponse<void>> {
    return Repository.post<void>(
      `${resource}/ResolveReview`,
      request,
      { withCredentials: true }
    );
  }
};
